import {
  Badge,
  Box,
  Button,
  Checkbox,
  Divider,
  Flex,
  Group,
  Modal,
  ScrollArea,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconFilter, IconPlus } from '@tabler/icons';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';

import { CustomDatePicker } from '../../components/custom-date-picker';
import { Page } from '../../components/page';
import {
  useGetEnrichedSectorLogs,
  useGetSectorDailyLog,
  useUpsertSectorDailyLog,
} from '../../data/hooks/sector-daily-logs';
import { useGetUsersPaginated } from '../../data/hooks/user';
import { EnrichedEmployee } from '../../data/sector-daily-logs';
import { formatLocale } from '../../providers/dayjs-plugins';
import { SECTOR_OPTIONS } from '../../utils/constants/sectors';

function toDateStr(d: Date) {
  return dayjs(d).format('YYYY-MM-DD');
}

const ROLE_LABEL: Record<string, string> = {
  'daily-employee': 'Diarista',
  'monthly-employee': 'Mensalista',
  employee: 'Registrado',
  'cleaning-employee': 'Limpeza',
  'manager-viewer': 'Gestor',
  'manager-creator': 'Gestor',
  none: '-',
};

const ROLE_COLOR: Record<string, string> = {
  'daily-employee': 'orange',
  'monthly-employee': 'blue',
  employee: 'green',
  'cleaning-employee': 'teal',
};

function RoleBadge({ role }: { role: string }) {
  return (
    <Badge size="xs" color={ROLE_COLOR[role] ?? 'gray'}>
      {ROLE_LABEL[role] ?? role}
    </Badge>
  );
}

function EmployeeRow({ emp }: { emp: EnrichedEmployee }) {
  return (
    <Group
      spacing="xs"
      px={8}
      py={6}
      style={{ borderBottom: '1px solid var(--mantine-color-gray-2,#e9ecef)' }}
      noWrap
    >
      <Text size="sm" fw={500} style={{ flex: 1, minWidth: 0 }} truncate>
        {emp.username}
      </Text>
      <Box style={{ width: 70, flexShrink: 0 }}>
        <Badge size="xs" variant="outline" color="gray">
          {emp.accessCode}
        </Badge>
      </Box>
      <Box style={{ width: 90, flexShrink: 0 }}>
        <RoleBadge role={emp.role} />
      </Box>
      <Box style={{ width: 90, flexShrink: 0 }}>
        {emp.workScaleName && (
          <Badge size="xs" color="violet" variant="light">
            {emp.workScaleName}
          </Badge>
        )}
      </Box>
    </Group>
  );
}

export function SectorDailyLogs() {
  const [registerOpen, setRegisterOpen] = useState(false);

  // Modal de registro
  const [date, setDate] = useState<Date>(new Date());
  const [selectedCodes, setSelectedCodes] = useState<Set<string>>(new Set());
  const [modalFilterCode, setModalFilterCode] = useState('');
  const [modalFilterSector, setModalFilterSector] = useState<string | null>(
    null,
  );

  // Filtros da consulta principal
  const [viewDate, setViewDate] = useState<Date>(new Date());
  const [filterSector, setFilterSector] = useState<string | null>(null);
  const [filterRole, setFilterRole] = useState<string | null>(null);
  const [filterName, setFilterName] = useState('');
  const [filterSupervisor, setFilterSupervisor] = useState('');

  const form = useForm({
    initialValues: { sector: '' },
    validate: { sector: (v) => (v ? null : 'Selecione o setor') },
  });

  const { upsert, loading: saving } = useUpsertSectorDailyLog();
  const { fetch: fetchLog, log } = useGetSectorDailyLog();
  const {
    fetch: fetchEnriched,
    logs: enrichedLogs,
    loading: loadingLogs,
  } = useGetEnrichedSectorLogs();
  const {
    fetch: fetchUsers,
    response: usersData,
    loading: loadingUsers,
  } = useGetUsersPaginated();

  useEffect(() => {
    fetchEnriched(toDateStr(viewDate), filterSector ?? undefined);
  }, []); // eslint-disable-line

  useEffect(() => {
    if (registerOpen) {
      fetchUsers({ query: { page: 1, limit: 500 } });
    }
  }, [registerOpen]); // eslint-disable-line

  useEffect(() => {
    if (form.values.sector && registerOpen) {
      fetchLog(toDateStr(date), form.values.sector).then(() => {});
    }
  }, [date, form.values.sector, registerOpen]); // eslint-disable-line

  useEffect(() => {
    setSelectedCodes(new Set(log?.employeeCodes ?? []));
  }, [log]);

  function applyFilters() {
    fetchEnriched(toDateStr(viewDate), filterSector ?? undefined);
  }

  function toggleCode(code: string) {
    setSelectedCodes((prev) => {
      const next = new Set(prev);
      next.has(code) ? next.delete(code) : next.add(code);
      return next;
    });
  }

  function handleSave() {
    const { hasErrors } = form.validate();
    if (hasErrors) return;
    upsert(
      {
        date: toDateStr(date),
        sector: form.values.sector,
        employeeCodes: Array.from(selectedCodes),
      },
      () => {
        setRegisterOpen(false);
        fetchEnriched(toDateStr(viewDate), filterSector ?? undefined);
      },
    );
  }

  function openRegister() {
    form.reset();
    setSelectedCodes(new Set());
    setModalFilterCode('');
    setModalFilterSector(null);
    setDate(new Date());
    setRegisterOpen(true);
  }

  const filteredUsers = useMemo(() => {
    const codeLower = modalFilterCode.trim().toLowerCase();
    return (usersData?.items ?? []).filter((u) => {
      if (codeLower && !u.accessCode?.toLowerCase().includes(codeLower))
        return false;
      if (modalFilterSector && (u as any).sector !== modalFilterSector)
        return false;
      return true;
    });
  }, [usersData, modalFilterCode, modalFilterSector]);

  const nameLower = filterName.trim().toLowerCase();
  const supervisorLower = filterSupervisor.trim().toLowerCase();

  const displayedLogs = enrichedLogs
    ?.filter((l) =>
      supervisorLower
        ? l.registeredBy?.username?.toLowerCase().includes(supervisorLower)
        : true,
    )
    .map((l) => ({
      ...l,
      employees: l.employees.filter(
        (e) =>
          (!filterRole || e.role === filterRole) &&
          (!nameLower || e.username.toLowerCase().includes(nameLower)),
      ),
    }));

  return (
    <Page title="Funcionários por Setor">
      {/* ── MODAL REGISTRAR ── */}
      <Modal
        opened={registerOpen}
        onClose={() => setRegisterOpen(false)}
        title="Registrar funcionários do setor"
        size="lg"
      >
        <Stack spacing="sm">
          <Group grow>
            <CustomDatePicker
              label="Data"
              value={date}
              onChange={(d) => d && setDate(d)}
              mb={0}
            />
            <Select
              label="Setor"
              placeholder="Selecione..."
              data={SECTOR_OPTIONS}
              {...form.getInputProps('sector')}
            />
          </Group>

          <Divider
            label="Selecionar funcionários"
            labelPosition="left"
            mt={4}
          />

          <Group spacing="xs">
            <TextInput
              placeholder="Filtrar por código"
              value={modalFilterCode}
              onChange={(e) => setModalFilterCode(e.currentTarget.value)}
              style={{ flex: 1 }}
            />
            <Select
              placeholder="Filtrar por setor"
              data={SECTOR_OPTIONS}
              clearable
              value={modalFilterSector}
              onChange={setModalFilterSector}
              style={{ width: 180 }}
            />
          </Group>

          {/* Cabeçalho */}
          <Group
            spacing="xs"
            px={8}
            py={4}
            style={{
              background: 'var(--mantine-color-gray-1,#f1f3f5)',
              borderRadius: 6,
            }}
            noWrap
          >
            <Box style={{ width: 24 }} />
            <Text size="xs" fw={600} style={{ width: 120 }}>
              Código
            </Text>
            <Text size="xs" fw={600} style={{ flex: 1 }}>
              Setor
            </Text>
          </Group>

          <ScrollArea h={300} offsetScrollbars>
            {loadingUsers && (
              <Text size="sm" color="dimmed" px={8}>
                Carregando...
              </Text>
            )}
            {!loadingUsers && filteredUsers.length === 0 && (
              <Text size="sm" color="dimmed" px={8}>
                Nenhum funcionário encontrado.
              </Text>
            )}
            {filteredUsers.map((u) => (
              <Group
                key={u.accessCode}
                spacing="xs"
                px={8}
                py={6}
                noWrap
                style={{
                  borderBottom: '1px solid var(--mantine-color-gray-2,#e9ecef)',
                  cursor: 'pointer',
                  background: selectedCodes.has(u.accessCode)
                    ? 'var(--mantine-color-blue-0,#e7f5ff)'
                    : undefined,
                }}
                onClick={() => toggleCode(u.accessCode)}
              >
                <Checkbox
                  checked={selectedCodes.has(u.accessCode)}
                  onChange={() => toggleCode(u.accessCode)}
                  onClick={(e) => e.stopPropagation()}
                  style={{ width: 24 }}
                />
                <Text size="sm" style={{ width: 120, fontFamily: 'monospace' }}>
                  {u.accessCode}
                </Text>
                <Text size="sm" color="dimmed" style={{ flex: 1 }}>
                  {(u as any).sector ?? '—'}
                </Text>
              </Group>
            ))}
          </ScrollArea>

          <Group position="apart">
            <Text size="xs" color="dimmed">
              {selectedCodes.size} selecionado
              {selectedCodes.size !== 1 ? 's' : ''}
            </Text>
            <Group spacing="xs">
              <Button variant="default" onClick={() => setRegisterOpen(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSave} loading={saving}>
                Salvar
              </Button>
            </Group>
          </Group>
        </Stack>
      </Modal>

      {/* ── CONSULTA PRINCIPAL ── */}
      <Stack spacing="sm" mt="md" px={16}>
        {/* Barra de filtros + botão registrar */}
        <Group spacing="sm" align="flex-end" noWrap>
          <Box style={{ flexShrink: 0 }}>
            <CustomDatePicker
              label="Data"
              value={viewDate}
              onChange={(d) => d && setViewDate(d)}
              mb={0}
            />
          </Box>
          <Select
            label="Setor"
            placeholder="Todos os setores"
            data={SECTOR_OPTIONS}
            clearable
            value={filterSector}
            onChange={setFilterSector}
            style={{ minWidth: 180, flexShrink: 0 }}
          />
          <Select
            label="Tipo"
            placeholder="Todos os tipos"
            clearable
            value={filterRole}
            onChange={setFilterRole}
            style={{ minWidth: 160, flexShrink: 0 }}
            data={[
              { value: 'daily-employee', label: 'Diarista' },
              { value: 'monthly-employee', label: 'Mensalista' },
              { value: 'employee', label: 'Registrado' },
              { value: 'cleaning-employee', label: 'Limpeza' },
            ]}
          />
          <TextInput
            label="Nome do funcionário"
            placeholder="Filtrar por nome"
            value={filterName}
            onChange={(e) => setFilterName(e.currentTarget.value)}
            style={{ minWidth: 180, flexShrink: 0 }}
          />
          <TextInput
            label="Supervisor"
            placeholder="Filtrar por supervisor"
            value={filterSupervisor}
            onChange={(e) => setFilterSupervisor(e.currentTarget.value)}
            style={{ minWidth: 180, flexShrink: 0 }}
          />
          <Button
            leftIcon={<IconFilter size={14} />}
            onClick={applyFilters}
            loading={loadingLogs}
            style={{ flexShrink: 0 }}
          >
            Filtrar
          </Button>
          <Box style={{ flex: 1 }} />
          <Button
            leftIcon={<IconPlus size={14} />}
            onClick={openRegister}
            style={{ flexShrink: 0 }}
          >
            Registrar
          </Button>
        </Group>

        <Divider />

        {loadingLogs && <Text color="dimmed">Carregando...</Text>}

        {!loadingLogs && (!displayedLogs || displayedLogs.length === 0) && (
          <Text color="dimmed">Nenhum registro para esta data.</Text>
        )}

        {displayedLogs?.map((log) => (
          <Box
            key={log.id}
            style={{
              border: '1px solid var(--mantine-color-gray-3)',
              borderRadius: 8,
            }}
          >
            <Flex
              px="sm"
              py={8}
              justify="space-between"
              align="center"
              style={{
                borderBottom: '1px solid var(--mantine-color-gray-2,#e9ecef)',
              }}
            >
              <Group spacing={8}>
                <Title order={5}>{log.sector}</Title>
                <Badge size="sm" color="gray" variant="filled">
                  {log.employees.length} funcionário
                  {log.employees.length !== 1 ? 's' : ''}
                </Badge>
              </Group>
              <Text size="xs" color="dimmed">
                Registrado por: {log.registeredBy?.username} ·{' '}
                {formatLocale(log.date, 'DD/MM/YYYY')}
              </Text>
            </Flex>

            {log.employees.length > 0 && (
              <Group
                spacing="xs"
                px={8}
                py={4}
                noWrap
                style={{ background: 'var(--mantine-color-gray-1,#f1f3f5)' }}
              >
                <Text
                  size="xs"
                  color="dimmed"
                  fw={600}
                  style={{ flex: 1, minWidth: 0 }}
                >
                  Nome
                </Text>
                <Text
                  size="xs"
                  color="dimmed"
                  fw={600}
                  style={{ width: 70, flexShrink: 0 }}
                >
                  Código
                </Text>
                <Text
                  size="xs"
                  color="dimmed"
                  fw={600}
                  style={{ width: 90, flexShrink: 0 }}
                >
                  Tipo
                </Text>
                <Text
                  size="xs"
                  color="dimmed"
                  fw={600}
                  style={{ width: 90, flexShrink: 0 }}
                >
                  Escala
                </Text>
              </Group>
            )}

            {log.employees.length === 0 && (
              <Text size="sm" color="dimmed" px="sm" py={8}>
                Nenhum funcionário neste setor com os filtros aplicados.
              </Text>
            )}

            {log.employees.map((emp) => (
              <EmployeeRow key={emp.accessCode} emp={emp} />
            ))}
          </Box>
        ))}
      </Stack>
    </Page>
  );
}
